import { SearchOutlined } from '@ant-design/icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Empty from 'antd/lib/empty';
import Flex from 'antd/lib/flex';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import Typography from 'antd/lib/typography';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ISan, ISite } from '../../../domain/site/interface';
import './smart-site-san-select.css';
import Row from 'antd/lib/row';
import difference from 'lodash/difference';

interface PrimalSelectOption<T = string> {
    sans: {
        value: T;
        label: string;
    }[];
    site: ISite;
    values: T[];
}

interface BaseFilterSelectCheckboxProps<T = string> {
    options: PrimalSelectOption<T>[];
    value: T[];
    onChange: (value: T[], checked: boolean, option?: PrimalSelectOption<T>) => void;

    disabled?: boolean;
    loading?: boolean;

    height?: number;

    showClear?: boolean;
    showResultsCount?: boolean;
    showSelectAllOption?: boolean;

    onClearAll?: (clearedOptions: PrimalSelectOption<T>[]) => void;
    onSelectAll?: (selectedOptions: PrimalSelectOption<T>[]) => void;
}

const MAX_OPTION_WIDTH = 235;
type TSiteSearchField = 'site_name' | 'san' | 'site_address';

export function SmartSiteSanSelect<T = string>({
    options,
    value,
    onChange,

    disabled = false,
    loading = false,

    showClear = false,
    showResultsCount = false,
    showSelectAllOption = false,

    height = 300,
    onClearAll = options => {},
    onSelectAll = options => {},
}: BaseFilterSelectCheckboxProps<T>) {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchField, setSearchField] = useState<TSiteSearchField>('site_name'); // Default search field
    const { filteredOptions, handleCheckboxChange, handleClearAll, handleSelectAll } = useFilterSelect(
        options,
        value,
        onChange,
        searchTerm,
        searchField,
        onClearAll,
        onSelectAll
    );

    return (
        <Flex vertical style={{ position: 'relative', padding: 0 }}>
            <Flex vertical align="center" style={{ padding: '8px', gap: '4px' }}>
                <Input
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search Site"
                    addonBefore={<SearchOutlined />}
                    style={{ backgroundColor: 'white', borderRadius: '5px' }}
                    addonAfter={<SiteSearchFieldSelect searchField={searchField} setSearchField={setSearchField} />}
                />

                <Flex justify={!showClear || !showSelectAllOption ? 'flex-end' : 'space-between'} style={{ width: '100%', marginTop: '8px' }}>
                    {showResultsCount && <Typography.Text>Results ({options.length})</Typography.Text>}

                    <Flex gap={16}>
                        {showSelectAllOption && (
                            <Button size="small" type="link" onClick={handleSelectAll}>
                                Select All
                            </Button>
                        )}

                        {showClear && (
                            <Button size="small" type="text" onClick={handleClearAll}>
                                Clear All
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Flex>

            <div style={{ overflow: 'hidden' }}>
                {loading && !disabled ? ( // Loading
                    <Flex justify="center" align="center" style={{ height: '100%' }}>
                        <Spin />
                    </Flex>
                ) : (!loading && filteredOptions.length === 0) || disabled ? ( // No results found or disabled
                    <Flex justify="center" align="center" style={{ height: '100%' }}>
                        <Empty
                            style={{ padding: '8px' }}
                            description={disabled ? 'Please select at least one filter to view available site options.' : 'No results found.'}
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </Flex>
                ) : (
                    // Render options
                    <VirtualizedOptionsList
                        height={height}
                        options={filteredOptions}
                        value={value}
                        disabled={disabled}
                        onCheckboxChange={handleCheckboxChange}
                    />
                )}
            </div>
        </Flex>
    );
}

interface ISiteSearchFieldSelectProps {
    searchField: TSiteSearchField;
    setSearchField: (field: TSiteSearchField) => void;
}

const SiteSearchFieldSelect: React.FC<ISiteSearchFieldSelectProps> = ({ searchField, setSearchField }) => {
    return (
        <Select
            size="small"
            allowClear={false}
            popupMatchSelectWidth={false}
            value={searchField}
            onChange={(value: TSiteSearchField) => setSearchField(value)}
            options={[
                { value: 'site_name', label: 'Name' },
                { value: 'san', label: 'SAN' },
                { value: 'site_address', label: 'Address' },
            ]}
        />
    );
};

function useFilterSelect<T>(
    options: PrimalSelectOption<T>[],
    value: T[],
    onChange: (value: T[], checked: boolean, option?: PrimalSelectOption<T>) => void,
    searchTerm: string,
    searchField: string = 'site_name',
    onClearAll?: (clearedOptions: PrimalSelectOption<T>[]) => void,
    onSelectAll?: (selectedOptions: PrimalSelectOption<T>[]) => void
) {
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;

        switch (searchField) {
            case 'site_name':
                return options.filter(opt => opt.site.site_name.toLowerCase().includes(searchTerm.toLowerCase()));
            case 'san':
                return options.filter(opt =>
                    opt.site.sans.find((san: ISan) => san.service_account_number.toLowerCase().includes(searchTerm.toLowerCase()))
                );
            case 'site_address':
                return options.filter(opt =>
                    (opt.site.site_address + ', ' + opt.site.site_city + ', ' + opt.site.site_state).toLowerCase().includes(searchTerm.toLowerCase())
                );
            default:
                return options;
        }
    }, [searchTerm, searchField, options]);

    const filteredOptionValues = useMemo(() => filteredOptions.flatMap(opt => opt.values), [filteredOptions]);

    const handleCheckboxChange = useCallback(
        (changedValues: T[], shouldAddValues: boolean, isAnyChildSelected: boolean, option?: PrimalSelectOption<T>) => {
            const newValue: T[] = shouldAddValues ? [...value, ...changedValues] : value.filter(v => !changedValues.includes(v));

            onChange(newValue, isAnyChildSelected, option);
        },
        [onChange, value]
    );

    const handleSelectAll = useCallback(() => {
        // Only select filtered options that aren't already selected
        const newSelections = filteredOptions.filter(option => !option.values.every(item => value.includes(item)));
        const newSelectionValues = newSelections.flatMap(option => option.values);

        // Combine with existing selections
        const updatedValue = [...value, ...newSelectionValues];

        // Update with all values and set checked to true
        onChange(updatedValue, true);

        onSelectAll && onSelectAll(newSelections);
    }, [filteredOptions, value, onChange, onSelectAll]);

    const handleClearAll = useCallback(() => {
        // Get values of filtered options
        const filteredOptionValues = filteredOptions.flatMap(option => option.values);

        // Keep only values that are not in the filtered options
        const remainingValues = value.filter(val => !filteredOptionValues.includes(val));

        // Update with remaining values and set checked to false
        onChange(remainingValues, false);

        // Notify about cleared options if callback exists
        onClearAll && onClearAll(filteredOptions);
    }, [filteredOptions, value, onChange, onClearAll]);

    return {
        filteredOptions,
        filteredOptionValues,
        handleCheckboxChange,
        handleClearAll,
        handleSelectAll,
    };
}

// Virtualized Options List Component
interface VirtualizedOptionsListProps<T = string> {
    height: number;
    options: PrimalSelectOption<T>[];
    value: T[];
    disabled: boolean;
    onCheckboxChange: (values: T[], shouldAddOptions: boolean, isAnyChildSelected: boolean, option: PrimalSelectOption<T>) => void;
}

export function VirtualizedOptionsList<T = string>({ height, options, value, disabled, onCheckboxChange }: VirtualizedOptionsListProps<T>) {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: options.length,
        getScrollElement: () => parentRef.current,
        estimateSize: index => calcVirtualOptionSize(options[index]),
        overscan: 5,
        useAnimationFrameWithResizeObserver: true,
        paddingEnd: 8,
    });

    return (
        <div
            ref={parentRef}
            style={{
                height,
                overflow: 'auto',
                paddingBottom: '8px',
            }}
        >
            <div
                style={{
                    height: virtualizer.getTotalSize(),
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map(virtualItem => {
                    const option = options[virtualItem.index];

                    const siteAvailableOptions = option.values;

                    const selectedSiteOptions = siteAvailableOptions.filter(opt => value.includes(opt));

                    const isAllSelected = siteAvailableOptions.length > 0 && siteAvailableOptions.length === selectedSiteOptions.length;
                    const noneSelected = selectedSiteOptions.length === 0;
                    const isIndeterminate = !isAllSelected && !noneSelected;

                    const onCheckbox = (newValues: T[]) => {
                        const isAnyChildSelected = !!newValues.length;
                        if (newValues.length > selectedSiteOptions.length) {
                            const valuesToAdd = difference(newValues, selectedSiteOptions);
                            onCheckboxChange(valuesToAdd, true, isAnyChildSelected, option);
                            return;
                        }

                        if (newValues.length < selectedSiteOptions.length) {
                            const valuesToARemove = difference(selectedSiteOptions, newValues);
                            onCheckboxChange(valuesToARemove, false, isAnyChildSelected, option);
                        }
                    };

                    return (
                        <div
                            key={virtualItem.key}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: virtualItem.size,
                                transform: `translateY(${virtualItem.start}px)`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            {/* Site-level checkbox */}
                            <Checkbox
                                disabled={disabled || !option.sans.length}
                                checked={isAllSelected}
                                onChange={e => onCheckbox(e.target.checked ? siteAvailableOptions : [])}
                                indeterminate={isIndeterminate}
                                rootClassName="site-select-checkbox"
                            >
                                <div
                                    style={{
                                        maxWidth: MAX_OPTION_WIDTH,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography.Text ellipsis={{ tooltip: { placement: 'right' } }} style={{ fontSize: '12px', lineHeight: 1.2 }}>
                                        {option.site.site_name} ({option.site.company?.company_name || ''})
                                    </Typography.Text>
                                    <Typography.Text
                                        type="secondary"
                                        ellipsis={{ tooltip: { placement: 'right' } }}
                                        style={{ fontSize: '11px', lineHeight: 1.1 }}
                                    >
                                        {option.values.length <= 1 && renderSiteSans(option.site)}
                                        {renderSiteAddress(option.site)}
                                    </Typography.Text>
                                </div>
                            </Checkbox>

                            {/* SAN checkboxes if site has multiple SANs*/}

                            {option.values.length > 1 && (
                                <Checkbox.Group value={selectedSiteOptions} onChange={onCheckbox} style={{ display: 'block' }}>
                                    {option.sans.map(san => (
                                        <Row key={san.label} style={{ paddingLeft: '24px' }}>
                                            <Checkbox value={san.value}>
                                                <Typography.Text style={{ fontSize: '11px' }}>{san.label}</Typography.Text>
                                            </Checkbox>
                                        </Row>
                                    ))}
                                </Checkbox.Group>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function calcVirtualOptionSize<T>(option: PrimalSelectOption<T>): number {
    const childOptions = option.values.length;
    const siteInfoBlockHeight = 44;
    const sanBlockHeight = 22;

    const totalSanBlocksHeight = sanBlockHeight * childOptions;

    return siteInfoBlockHeight + (childOptions > 1 ? totalSanBlocksHeight : 0);
}

function renderSiteSans(site: ISite): string {
    const sanStr = site.sans.map(san => san.service_account_number).join(', ');

    return sanStr ? `${sanStr}. ` : '';
}

function renderSiteAddress(site: ISite): string {
    return `${site.site_address}, ${site.site_city}, ${site.site_state}`;
}
